import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Card, CardActionArea, CardContent, Chip, Container, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useNavigate } from 'react-router-dom';
import { tutorialsAPI } from '../services/api';
import { HelpCategory, TutorialSummary, audienceText, durationText } from '../components/help/helpTypes';

const errText = (e: any): string => e?.response?.data?.message || e?.message || 'Request failed';

/** Help & Support: tutorials the signed-in person may see. The server filters by role, access and draft status. */
const HelpSupportPage: React.FC = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [tutorials, setTutorials] = useState<TutorialSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await tutorialsAPI.list({ q: q.trim() || undefined, category: category || undefined });
      setCategories(d.categories);
      setTutorials(d.tutorials);
      setError(null);
    } catch (e) { setError(errText(e)); } finally { setLoaded(true); }
  }, [q, category]);
  useEffect(() => { const t = setTimeout(() => { void load(); }, 200); return () => clearTimeout(t); }, [load]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Help & Support</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Short video tutorials and written steps for the parts of TEKS TIME you use. They use demonstration data only.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <TextField
        fullWidth size="small" placeholder="Search tutorials" value={q} onChange={(e) => setQ(e.target.value)} sx={{ mb: 2, maxWidth: 520 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} inputProps={{ 'aria-label': 'Search tutorials' }}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <Chip clickable label="All" color={category === '' ? 'primary' : 'default'} variant={category === '' ? 'filled' : 'outlined'} onClick={() => setCategory('')} />
        {categories.map((c) => (
          <Chip key={c.id} clickable label={`${c.title} (${c.count})`} color={category === c.id ? 'primary' : 'default'} variant={category === c.id ? 'filled' : 'outlined'} onClick={() => setCategory(c.id)} />
        ))}
      </Stack>
      {categories.filter((c) => tutorials.some((t) => t.category === c.id)).map((c) => (
      <Box key={c.id} component="section" aria-labelledby={`help-module-${c.id}`} sx={{ mb: 4 }}>
        <Typography id={`help-module-${c.id}`} variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>{c.title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        {tutorials.filter((t) => t.category === c.id).map((t) => (
          <Card key={t.id} variant="outlined">
            <CardActionArea onClick={() => navigate(`/help/${t.slug}`)} sx={{ height: '100%', alignItems: 'stretch' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <PlayCircleOutlineIcon color={t.playable ? 'primary' : 'disabled'} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>{t.title}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t.description}</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {t.menuOption && <Chip size="small" color="primary" variant="outlined" label={t.menuOption} />}
                  {t.status === 'draft' && <Chip size="small" color="warning" label="Draft: only you see this" />}
                  {t.playable ? (t.durationSeconds ? <Chip size="small" variant="outlined" label={durationText(t.durationSeconds)} /> : null) : <Chip size="small" variant="outlined" label="Video coming soon" />}
                  <Chip size="small" variant="outlined" label={audienceText(t.audience)} />
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
      </Box>
      ))}
      {loaded && !tutorials.length && !error && (
        <Typography color="text.secondary" sx={{ mt: 2 }}>{q || category ? 'No tutorials match.' : 'There are no tutorials for you yet. They will appear here as they are published.'}</Typography>
      )}
    </Container>
  );
};

export default HelpSupportPage;
